import { getPermissionsFromRoles, canAccessWithPermissions } from '@react-admin/ra-rbac'
import { clear } from 'idb-keyval'

import permissions from '../permissions'
import authProvider from './authProvider'
import { supabaseClient } from './'

const ACTION_MAP = {
  can_list: ['list'],
  can_show: ['show'],
  can_read: ['read'],
  can_create: ['create'],
  can_update: ['edit', 'write'],
  can_delete: ['delete'],
  can_export: ['export'],
}

const buildOverridePermissions = (rightsRows) => {
  const overriddenResources = new Set(rightsRows.map(r => r.resource))
  const overridePerms = []

  rightsRows.forEach(right => {
    const allowActions = []
    const denyActions = []

    Object.entries(ACTION_MAP).forEach(([column, actions]) => {
      if (right[column]) {
        allowActions.push(...actions)
      } else {
        denyActions.push(...actions)
      }
    })

    if (allowActions.length > 0) {
      overridePerms.push({ action: allowActions, resource: right.resource })
    }

    if (denyActions.length > 0) {
      overridePerms.push({ type: 'deny', action: denyActions, resource: right.resource })
    }
  })

  return { overriddenResources, overridePerms }
}

let cachedIdentity = null
let cachedPermissions = null
let identityPromise = null

let _authError = null
const _authErrorListeners = new Set()

export const authErrorStore = {
  subscribe: (listener) => {
    _authErrorListeners.add(listener)
    return () => _authErrorListeners.delete(listener)
  },
  getSnapshot: () => _authError,
}

export const clearIdentityCache = () => {
  cachedIdentity = null
  identityPromise = null
}

export const getCachedIdentity = () => cachedIdentity

const customAuthProvider = () => {
  // Now these functions use the variables defined above
  const getIdentityOnce = async () => {
    if (identityPromise) {
      return identityPromise
    }
    if (cachedIdentity) {
      return cachedIdentity
    }

    identityPromise = authProvider.getIdentity()
      .then(identity => {
        if (identity?.role?.grade === undefined || identity?.role?.grade === null) {
          throw new Error('Invalid identity: missing role.grade')
        }
        cachedIdentity = identity
        _authError = null
        return identity
      })
      .catch(error => {
        console.error('Failed to load identity:', error)
        cachedIdentity = null
        if (error.message === 'Invalid identity: missing role.grade') {
          _authError = "Votre compte n'a pas de rôle attribué. Veuillez contacter un responsable."
          _authErrorListeners.forEach(fn => fn())
        }
        throw error
      })
      .finally(() => {
        identityPromise = null
      })

    return identityPromise
  }

  const getPermissions = async () => {
    const identity = await getIdentityOnce()
    const roleGrade = identity?.role?.grade

    if (roleGrade === undefined || roleGrade === null) {
      return []
    }

    if (cachedPermissions && cachedPermissions._roleGrade === roleGrade) {
      return cachedPermissions
    }

    // Start with grade-based permissions
    let gradePerms = getPermissionsFromRoles({
      roleDefinitions: permissions,
      userRoles: [roleGrade],
    })

    // Fetch per-profile overrides
    const { data: rightsData, error } = await supabaseClient
      .from('profiles_rights')
      .select('*')
      .eq('profile_id', identity.id)

    if (!error && rightsData && rightsData.length > 0) {
      const { overriddenResources, overridePerms } = buildOverridePermissions(rightsData)

      // Remove grade rules for overridden resources (keep wildcard rules)
      gradePerms = gradePerms.filter(p => p.resource === '*' || !overriddenResources.has(p.resource))

      // Append override rules
      gradePerms = [...gradePerms, ...overridePerms]
    }

    cachedPermissions = gradePerms
    cachedPermissions._roleGrade = roleGrade

    return cachedPermissions
  }

  return {
    ...authProvider,
    getIdentity: getIdentityOnce,
    canAccess: async ({ resource, action, record }) => {
      const perms = await getPermissions()
      return canAccessWithPermissions({ permissions: perms, resource, action, record })
    },
    logout: async () => {
      // Clear the module-level caches
      cachedIdentity = null
      cachedPermissions = null
      identityPromise = null
      await clear()
      localStorage.clear()
      return authProvider.logout()
    },
    getPermissions
  }
}

export default customAuthProvider
